import React, { useState, useMemo, useEffect } from "react";
import TopMenu from "../../components/TopMenu/TopMenu";
import SearchableCombobox from "../../components/SearchableCombobox/SearchableCombobox";
import usePlantao from "./usePlantao";
import { TeamEntryForm } from "./usePlantao";
import { SelectableUser } from "../../api/UsersApi";
import { SHIFT_STATUS_OPTIONS, ShiftStatus } from "../../types/shiftStatus";
import { ShiftMode } from "../../types/shiftMode";
import { getPlaceCode } from "../../api/PlacesApi";
import "./Plantao.css";

function getUserLabel(user: SelectableUser): string {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || user.id;
}

type TeamEntryRowProps = {
  entry: TeamEntryForm;
  index: number;
  total: number;
  interviewerUsers: SelectableUser[];
  assistantUsers: SelectableUser[];
  updateTeamEntry: (localId: string, field: keyof Omit<TeamEntryForm, "localId" | "id">, value: string) => void;
  duplicateTeamEntry: (localId: string) => void;
  removeTeamEntry: (localId: string) => void;
};

function TeamEntryRow({
  entry,
  index,
  total,
  interviewerUsers,
  assistantUsers,
  updateTeamEntry,
  duplicateTeamEntry,
  removeTeamEntry
}: TeamEntryRowProps) {
  const [interviewerSearch, setInterviewerSearch] = useState("");
  const [assistantSearch, setAssistantSearch] = useState("");

  useEffect(() => {
    if (!entry.interviewerUserId) return;
    const user = interviewerUsers.find((u) => u.id === entry.interviewerUserId);
    if (user) setInterviewerSearch(getUserLabel(user));
  }, [entry.interviewerUserId, interviewerUsers]);

  useEffect(() => {
    if (!entry.assistantUserId) return;
    const user = assistantUsers.find((u) => u.id === entry.assistantUserId);
    if (user) setAssistantSearch(getUserLabel(user));
  }, [entry.assistantUserId, assistantUsers]);

  const filteredInterviewers = useMemo(() => {
    const term = interviewerSearch.trim().toLowerCase();
    if (!term) return interviewerUsers;
    return interviewerUsers.filter((u) => getUserLabel(u).toLowerCase().includes(term));
  }, [interviewerUsers, interviewerSearch]);

  const filteredAssistants = useMemo(() => {
    const term = assistantSearch.trim().toLowerCase();
    if (!term) return assistantUsers;
    return assistantUsers.filter((u) => getUserLabel(u).toLowerCase().includes(term));
  }, [assistantUsers, assistantSearch]);

  return (
    <div className="plantao-team-entry">
      <div className="plantao-team-entry-header">
        <span className="plantao-team-entry-label">Entrevista {index + 1}</span>
        <div className="plantao-team-entry-actions">
          <button
            type="button"
            className="plantao-duplicate-btn"
            onClick={() => duplicateTeamEntry(entry.localId)}
          >
            Duplicar
          </button>
          <button
            type="button"
            className="plantao-remove-btn"
            onClick={() => removeTeamEntry(entry.localId)}
            disabled={total <= 1}
          >
            Remover
          </button>
        </div>
      </div>
      <div className="plantao-grid">
        <div className="plantao-field">
          <label>Data de início</label>
          <input
            type="date"
            value={entry.startDate}
            onChange={(e) => updateTeamEntry(entry.localId, "startDate", e.target.value)}
            max={entry.endDate || undefined}
            required
          />
        </div>
        <div className="plantao-field">
          <label>Hora de início</label>
          <input
            type="time"
            value={entry.startTime}
            onChange={(e) => updateTeamEntry(entry.localId, "startTime", e.target.value)}
            max={entry.startDate === entry.endDate ? entry.endTime : undefined}
            required
          />
        </div>
        <div className="plantao-field">
          <label>Data de término</label>
          <input
            type="date"
            value={entry.endDate}
            onChange={(e) => updateTeamEntry(entry.localId, "endDate", e.target.value)}
            min={entry.startDate || undefined}
            required
          />
        </div>
        <div className="plantao-field">
          <label>Hora de término</label>
          <input
            type="time"
            value={entry.endTime}
            onChange={(e) => updateTeamEntry(entry.localId, "endTime", e.target.value)}
            min={entry.startDate === entry.endDate ? entry.startTime : undefined}
            required
          />
        </div>
        <div className="plantao-field">
          <SearchableCombobox
            id={`interviewerCombobox-${entry.localId}`}
            label="Entrevistador"
            value={interviewerSearch}
            selectedValue={entry.interviewerUserId}
            placeholder="Selecione um entrevistador"
            options={filteredInterviewers.map((u) => ({ value: u.id, label: getUserLabel(u) }))}
            noOptionsText="Nenhum entrevistador encontrado."
            onInputChange={(v) => {
              setInterviewerSearch(v);
              updateTeamEntry(entry.localId, "interviewerUserId", "");
            }}
            onSelect={(opt) => {
              setInterviewerSearch(opt.label);
              updateTeamEntry(entry.localId, "interviewerUserId", opt.value);
            }}
            onClear={() => {
              setInterviewerSearch("");
              updateTeamEntry(entry.localId, "interviewerUserId", "");
            }}
            onBlur={() => {
              if (!entry.interviewerUserId) setInterviewerSearch("");
            }}
          />
        </div>
        <div className="plantao-field">
          <SearchableCombobox
            id={`assistantCombobox-${entry.localId}`}
            label="Assistente"
            value={assistantSearch}
            selectedValue={entry.assistantUserId}
            placeholder="Selecione um assistente"
            options={filteredAssistants.map((u) => ({ value: u.id, label: getUserLabel(u) }))}
            noOptionsText="Nenhum assistente encontrado."
            onInputChange={(v) => {
              setAssistantSearch(v);
              updateTeamEntry(entry.localId, "assistantUserId", "");
            }}
            onSelect={(opt) => {
              setAssistantSearch(opt.label);
              updateTeamEntry(entry.localId, "assistantUserId", opt.value);
            }}
            onClear={() => {
              setAssistantSearch("");
              updateTeamEntry(entry.localId, "assistantUserId", "");
            }}
            onBlur={() => {
              if (!entry.assistantUserId) setAssistantSearch("");
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function Plantao() {
  const {
    isEditing,
    loading,
    saving,
    error,
    mode,
    setMode,
    status,
    setStatus,
    cityId,
    setCityId,
    citySearch,
    setCitySearch,
    filteredCities,
    cityNeedsSelection,
    languageId,
    setLanguageId,
    languages,
    facilitatorUserId,
    setFacilitatorUserId,
    facilitatorSearch,
    setFacilitatorSearch,
    filteredFacilitatorUsers,
    interviewerUsers,
    assistantUsers,
    teamEntries,
    addTeamEntry,
    updateTeamEntry,
    duplicateTeamEntry,
    removeTeamEntry,
    canSubmit,
    submitDisabledReason,
    handleSubmit
  } = usePlantao();

  if (loading) {
    return (
      <div className="plantao-page">
        <TopMenu />
        <div className="plantao-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="plantao-page">
      <TopMenu />

      <main className="plantao-content">
        <h1 className="plantao-title">
          {isEditing ? "Editar Plantão" : "Cadastrar Plantão"}
        </h1>

        {error && <div className="plantao-error">{error}</div>}

        <form className="plantao-form" onSubmit={handleSubmit}>

          {/* Mode toggle */}
          <div className="plantao-toggle-group">
            <span className={`plantao-toggle-label ${mode === ShiftMode.Online ? "active" : ""}`}>
              Online
            </span>
            <button
              type="button"
              className={`plantao-toggle ${mode === ShiftMode.Presential ? "presencial" : "online"}`}
              onClick={() => setMode(mode === ShiftMode.Online ? ShiftMode.Presential : ShiftMode.Online)}
            >
              <span className="plantao-toggle-slider" />
            </button>
            <span className={`plantao-toggle-label ${mode === ShiftMode.Presential ? "active" : ""}`}>
              Presencial
            </span>
          </div>

          {/* Status */}
          <div className="plantao-field">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ShiftStatus)}
            >
              {SHIFT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

            <div className="plantao-field">
              <SearchableCombobox
                id="placeCombobox"
                label={mode === ShiftMode.Presential ? "Local" : "Local de referência"}
                value={citySearch}
                selectedValue={cityId}
                placeholder="Selecione um local"
                options={filteredCities.map((c) => ({
                  value: getPlaceCode(c),
                  label: String((c as any).name ?? getPlaceCode(c))
                }))}
                isInvalid={cityNeedsSelection}
                warningText="Selecione um local da lista."
                noOptionsText="Nenhum local encontrado."
                onInputChange={(value) => {
                  setCitySearch(value);
                  setCityId("");
                }}
                onSelect={(option) => {
                  setCityId(option.value);
                  setCitySearch(option.label);
                }}
                onClear={() => {
                  setCitySearch("");
                  setCityId("");
                }}
                onBlur={() => {
                  if (!cityId) {
                    setCitySearch("");
                  }
                }}
              />
            </div>
            
            <div className="plantao-field">
                <label htmlFor="languageId">Idioma</label>
                <select id="languageId" value={languageId} onChange={(e) => setLanguageId(e.target.value)}>
                <option value="">Selecione um idioma</option>
                {languages.map((l) => (
                    <option key={l.id} value={l.id}>
                    {l.name}
                    </option>
                ))}
                </select>
            </div>

            <div className="plantao-field">
              <SearchableCombobox
                id="facilitatorCombobox"
                label="Facilitador"
                value={facilitatorSearch}
                selectedValue={facilitatorUserId}
                placeholder="Selecione um facilitador"
                options={filteredFacilitatorUsers.map((u) => ({ value: u.id, label: getUserLabel(u) }))}
                noOptionsText="Nenhum facilitador encontrado."
                onInputChange={(v) => {
                  setFacilitatorSearch(v);
                  setFacilitatorUserId("");
                }}
                onSelect={(opt) => {
                  setFacilitatorSearch(opt.label);
                  setFacilitatorUserId(opt.value);
                }}
                onClear={() => {
                  setFacilitatorSearch("");
                  setFacilitatorUserId("");
                }}
                onBlur={() => {
                  if (!facilitatorUserId) setFacilitatorSearch("");
                }}
              />
            </div>



          {/* Shift team entries */}
          <div className="plantao-team-section">
            <div className="plantao-team-header">
              <span className="plantao-section-label">Entrevistas</span>
              <button type="button" className="plantao-add-btn" onClick={addTeamEntry}>
                + Adicionar entrevista
              </button>
            </div>

            {teamEntries.length === 0 && (
              <p className="plantao-team-empty">Nenhuma entrevista adicionada.</p>
            )}

            {teamEntries.map((entry: TeamEntryForm, index: number) => (
              <TeamEntryRow
                key={entry.localId}
                entry={entry}
                index={index}
                total={teamEntries.length}
                interviewerUsers={interviewerUsers}
                assistantUsers={assistantUsers}
                updateTeamEntry={updateTeamEntry}
                duplicateTeamEntry={duplicateTeamEntry}
                removeTeamEntry={removeTeamEntry}
              />
            ))}
          </div>

          {/* Actions */}
          {!canSubmit && !saving && submitDisabledReason && (
            <p className="plantao-submit-reason">{submitDisabledReason}</p>
          )}
          <div className="plantao-actions">
            <button type="submit" className="plantao-btn-primary" disabled={saving || !canSubmit}>
              {saving ? "Salvando..." : isEditing ? "Salvar alterações" : "Cadastrar"}
            </button>
            <button
              type="button"
              className="plantao-btn-secondary"
              onClick={() => window.history.back()}
              disabled={saving}
            >
              Cancelar
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
